import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { pillarForAnchor, PILLAR_LABEL, type Pillar } from '../lib/surfaceRegistry';
import { captureEvent } from '../lib/analytics';

interface Step {
  // CSS selector for the element to highlight. Resolved fresh on each step
  // so route changes / lazy renders are picked up. Null = centered modal
  // with no target (used for the closing "you're all set" slide).
  target: string | null;
  title: string;
  body: string;
  // Where to anchor the tooltip relative to the target. Ignored when target is null.
  placement?: 'bottom' | 'top' | 'right' | 'left';
  // Optional route to navigate to before this step is shown. Supports query
  // strings (e.g. `/intelligence?tab=insights`). Null = stay on current route.
  route?: string | null;
}

interface Props {
  open: boolean;
  steps: Step[];
  logoUrl?: string;
  onClose: () => void;       // user closed before finishing
  onComplete: () => void;    // user reached the end
}

interface Rect { top: number; left: number; width: number; height: number }

const PAD = 6;                // padding around the highlighted target
const TOOLTIP_GAP = 12;       // gap between target and tooltip
const TOOLTIP_W = 320;
const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 2000; // how long to wait for a target element to mount

// Placement-math fallback until the tooltip's real height is measured. Sized
// to fit the LONGEST step body so the over-estimate can only reserve too much
// margin, never clip the Next button off-viewport (user report 2026-05-24).
const TOOLTIP_H_ESTIMATE = 360;

// Pillar chip dot colors — brand tokens only (brand-centralization rule).
const PILLAR_DOT: Record<Pillar, string> = {
  see: 'bg-brand-accent',
  plan: 'bg-brand-primary',
  catch: 'bg-brand-spark',
};

export default function GuidedTour({ open, steps, logoUrl, onClose, onComplete }: Props) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  // Tooltip height used by the placement math. Starts at the conservative
  // estimate, then snaps to the real rendered height via a ResizeObserver on
  // the tooltip itself (fixes short steps floating slightly off-anchor). The
  // functional updater bails out when the value is unchanged — the same
  // equality-bail pattern as applyRect below, which is what makes runtime
  // measurement safe from the React #300 infinite re-render that killed the
  // earlier deps-less useLayoutEffect measurement attempt (2026-05-24).
  const [tooltipH, setTooltipH] = useState(TOOLTIP_H_ESTIMATE);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  // Reset to step 0 every time the tour reopens — never resume mid-tour.
  useEffect(() => { if (open) setIdx(0); }, [open]);

  // Drive page navigation when a step specifies a route. We compare against
  // the current location to avoid a noisy no-op nav (same route would still
  // remount some components and lose scroll position).
  useEffect(() => {
    if (!open || !step) return;
    if (!step.route) return;
    const currentFull = `${location.pathname}${location.search}`;
    if (currentFull === step.route) return;
    navigate(step.route);
  }, [open, step, location.pathname, location.search, navigate]);

  // Find + measure the target element. Polls for up to 2s in case the target
  // hasn't mounted yet (lazy route, suspense boundary, late filter render).
  // Also re-measures on resize/scroll so the highlight tracks layout shifts.
  useLayoutEffect(() => {
    if (!open || !step) return;
    if (!step.target) { setRect(null); return; }

    let cancelled = false;
    let pollTimer: number | undefined;
    const start = Date.now();

    // Functional updater + field-by-field equality. Critical: React bails
    // out when setState returns the previous reference, so this prevents
    // the infinite-re-render loop (React error #300) that fired when the
    // ResizeObserver below re-observed the target on every parent render.
    const applyRect = (r: { top: number; left: number; width: number; height: number }) => {
      setRect(prev => {
        if (prev
          && prev.top    === r.top
          && prev.left   === r.left
          && prev.width  === r.width
          && prev.height === r.height) {
          return prev;
        }
        return { top: r.top, left: r.left, width: r.width, height: r.height };
      });
    };

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target!) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        applyRect(r);
        // Tall targets (Workload board, Sprint Forecast Matrix, etc.) need
        // 'start' so the user sees the top of the surface — column headers,
        // PERSON column header — not the middle of a column. block:'center'
        // would shove the middle of a 1500px element to the viewport center,
        // hiding the contextual top. Small targets keep 'center'.
        const targetTooTall = r.height > window.innerHeight * 0.8;
        el.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: targetTooTall ? 'start' : 'center',
          inline: 'nearest',
        });
      } else if (Date.now() - start < POLL_TIMEOUT_MS) {
        pollTimer = window.setTimeout(measure, POLL_INTERVAL_MS);
      } else {
        // Couldn't find the target — show a centered tooltip rather than
        // breaking the tour silently, and TELL US: a missing anchor in prod
        // is IA drift the contract tests didn't cover (renamed tag, tab-gated
        // target, per-org customized step pointing at removed UI).
        captureEvent('tour target missing', { target: step.target, route: step.route ?? null });
        setRect(null);
      }
    };
    measure();

    const onLayoutChange = () => {
      if (!step.target) return;
      const el = document.querySelector(step.target) as HTMLElement | null;
      if (!el) return;
      applyRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', onLayoutChange);
    window.addEventListener('scroll', onLayoutChange, true);

    // Re-measure when the target element itself grows or shrinks. This is the
    // fix for the "highlight is too short" bug on data-driven surfaces like
    // the Sprint Forecast Matrix: the wrapper element is measured at mount
    // BEFORE the matrix data lands, so the initial rect reflects the empty
    // / instructional-text-only size. Once the rows render, the wrapper
    // grows but resize/scroll events don't fire — only ResizeObserver does.
    let observer: ResizeObserver | null = null;
    const el = document.querySelector(step.target!) as HTMLElement | null;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(onLayoutChange);
      observer.observe(el);
    }

    return () => {
      cancelled = true;
      if (pollTimer) window.clearTimeout(pollTimer);
      window.removeEventListener('resize', onLayoutChange);
      window.removeEventListener('scroll', onLayoutChange, true);
      if (observer) observer.disconnect();
    };
  }, [open, step, idx]);

  // Measure the tooltip's real height so the placement math stops guessing.
  // Keyed on idx (content changes per step); the equality-bail setState plus
  // integer rounding prevents the setState→render→observe feedback loop.
  useLayoutEffect(() => {
    if (!open) return;
    const el = tooltipRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measureTooltip = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > 0) setTooltipH(prev => (prev === h ? prev : h));
    };
    measureTooltip();
    const ro = new ResizeObserver(measureTooltip);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, idx]);

  // Keyboard navigation: → next / finish, ← back, Esc skip. Ignores keys
  // typed into form fields (the page under the tour stays interactive).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (idx === steps.length - 1) onComplete();
        else setIdx(i => Math.min(i + 1, steps.length - 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIdx(i => Math.max(i - 1, 0));
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, idx, steps.length, onComplete, onClose]);

  if (!open || !step) return null;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Pillar chip — derived from the step's anchor via the surface registry
  // (never stored on the step row), so IA reorders can't strand stale chips.
  const pillar = pillarForAnchor(step.target);

  // Tooltip position. Strategy:
  //   1. Try the requested placement first.
  //   2. If it doesn't fit in the viewport, walk through the other three.
  //   3. If NOTHING fits (huge target filling the viewport — e.g. the full
  //      Sprint Forecast Matrix), park the tooltip in the bottom-right
  //      corner so it doesn't cover the content the user is supposed to be
  //      looking at within the highlight.
  const tooltipPos = (() => {
    const winW = window.innerWidth;
    const winH = window.innerHeight;
    const MARGIN = 12;
    if (!rect) {
      return { top: winH / 2 - tooltipH / 2, left: winW / 2 - TOOLTIP_W / 2 };
    }

    const tryPlace = (p: 'bottom' | 'top' | 'right' | 'left'): { top: number; left: number; fits: boolean } => {
      let top = 0;
      let left = 0;
      switch (p) {
        case 'bottom':
          top = rect.top + rect.height + TOOLTIP_GAP;
          left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
          break;
        case 'top':
          top = rect.top - TOOLTIP_GAP - tooltipH;
          left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
          break;
        case 'right':
          top = rect.top + rect.height / 2 - tooltipH / 2;
          left = rect.left + rect.width + TOOLTIP_GAP;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tooltipH / 2;
          left = rect.left - TOOLTIP_GAP - TOOLTIP_W;
          break;
      }
      const fits = top >= MARGIN
                && top + tooltipH <= winH - MARGIN
                && left >= MARGIN
                && left + TOOLTIP_W <= winW - MARGIN;
      return { top, left, fits };
    };

    const requested = (step.placement || 'bottom') as 'bottom' | 'top' | 'right' | 'left';
    // Fallback order — try requested first, then the opposite axis, then the
    // perpendicular axis. Picking "opposite first" means a bottom-requested
    // placement that doesn't fit flips to top before going sideways, which is
    // less jarring visually.
    const fallback = {
      bottom: ['bottom', 'top',    'right', 'left'],
      top:    ['top',    'bottom', 'right', 'left'],
      right:  ['right',  'left',   'bottom', 'top'],
      left:   ['left',   'right',  'bottom', 'top'],
    }[requested] as Array<'bottom' | 'top' | 'right' | 'left'>;

    for (const p of fallback) {
      const result = tryPlace(p);
      if (result.fits) {
        return {
          top: Math.max(MARGIN, Math.min(result.top, winH - tooltipH - MARGIN)),
          left: Math.max(MARGIN, Math.min(result.left, winW - TOOLTIP_W - MARGIN)),
        };
      }
    }

    // Corner fallback — target is bigger than any side has room for.
    // Bottom-right keeps the tooltip out of the way of typical content
    // (which reads top-to-bottom, left-to-right).
    return {
      top: winH - tooltipH - MARGIN,
      left: winW - TOOLTIP_W - MARGIN,
    };
  })();

  const handleNext = () => {
    if (isLast) onComplete();
    else setIdx(i => i + 1);
  };

  const handleBack = () => {
    if (idx > 0) setIdx(i => i - 1);
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] pointer-events-none">
      {/* Backdrop. The cut-out around the target uses an outset box-shadow so
          we don't need an SVG mask: the highlighted div has no fill, but its
          shadow paints the rest of the screen at 55% black. */}
      {rect ? (
        <div
          className={`absolute pointer-events-none rounded-lg ${reduceMotion ? '' : 'transition-all duration-200'}`}
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
            outline: '3px solid #14B8A6',
            outlineOffset: '2px',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/55 pointer-events-auto" onClick={onClose} />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="absolute bg-card rounded-lg shadow-2xl pointer-events-auto overflow-hidden"
        style={{ top: tooltipPos.top, left: tooltipPos.left, width: TOOLTIP_W }}
      >
        <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-line-soft">
          {logoUrl && <img src={logoUrl} alt="" className="h-5 max-w-[80px] object-contain" />}
          <span className="text-xs font-bold text-ink-faint tracking-normal">
            Step {idx + 1} of {steps.length}
          </span>
          <button
            onClick={onClose}
            className="ml-auto text-ink-faint hover:text-ink-muted text-sm leading-none"
            aria-label="Skip tour"
          >
            ✕
          </button>
        </div>
        <div className="h-0.5 bg-brand-spark" />
        <div className="px-4 py-3">
          {pillar && (
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${PILLAR_DOT[pillar]}`} aria-hidden />
              <span className="text-[9.5px] font-black uppercase tracking-wide text-ink-faint">{PILLAR_LABEL[pillar]}</span>
            </div>
          )}
          <h3 className="text-lg font-extrabold text-ink leading-tight mb-2">{step.title}</h3>
          <p className="text-sm text-ink-muted leading-relaxed">{step.body}</p>
        </div>
        <div className="px-4 pb-3 pt-1 flex items-center justify-between gap-2">
          <button
            onClick={onClose}
            className="text-xs text-ink-faint hover:text-ink-muted"
          >
            Skip tour
          </button>
          {/* Progress dots — jump anywhere; the route effect handles any nav. */}
          <div className="flex items-center gap-1" aria-label="Tour progress">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Go to step ${i + 1}`}
                aria-current={i === idx || undefined}
                className={`rounded-full ${reduceMotion ? '' : 'transition-all duration-200'} ${
                  i === idx ? 'w-4 h-1.5 bg-brand-spark' : 'w-1.5 h-1.5 bg-line hover:bg-ink-faint'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button
                onClick={handleBack}
                className="text-xs px-3 py-1.5 rounded border border-line text-ink-muted hover:bg-wash"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="text-xs px-3 py-1.5 rounded bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold"
            >
              {isLast ? 'Finish' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
